// Utility helpers for chat tools and capabilities
//
// Single source of truth for deciding when "text tools" like
// `search-web` and `study-and-learn` should be disabled based on
// the currently selected models and agents.
//
// Rule:
// - If ANY selected model or agent produces non-text output
//   (images, audio, or video), text-focused tools are disabled.
//
// This file is intentionally free of React hooks and store access so
// it can be reused from components, stores, and other utilities.

/**
 * Determine if text tools should be disabled given the selected models
 * and agents. Intended for use on the client where we already have
 * the concrete model/agent objects.
 *
 * @param {Object} params
 * @param {Array<Object>} params.selectedModels - Array of selected model objects
 * @param {Array<Object>} params.selectedAgents - Array of selected agent objects
 *
 * @returns {boolean} true if any selection supports non-text outputs
 */
export const areTextToolsDisabledForSelection = ({
  selectedModels = [],
  selectedAgents = [],
} = {}) => {
  try {
    const hasNonTextModelOutput = Array.isArray(selectedModels) && selectedModels.some((model) => {
      const output = model?.capabilities?.output;
      if (!output || typeof output !== 'object') return false;
      // Treat image/audio/video outputs as "non-text" modalities.
      return !!(output.hasImages || output.hasAudio || output.hasVideo);
    });

    const hasNonTextAgentOutput = Array.isArray(selectedAgents) && selectedAgents.some((agent) => {
      const types = agent?.outputTypes;
      if (!Array.isArray(types)) return false;
      // Any declared output type that isn't plain "text" counts as non-text.
      return types.some((t) => t && t !== 'text');
    });

    return hasNonTextModelOutput || hasNonTextAgentOutput;
  } catch (e) {
    // On any unexpected error, fall back to not disabling tools.
    // This is safer than over-disabling functionality.
    console.log('[tools] areTextToolsDisabledForSelection error:', e);
    return false;
  }
};


